import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTracking } from 'react-tracking';
import { useDispatch } from 'react-redux';

import {
  Grid,
  Text,
  useTheme,
  ButtonWithDropdown,
  Box,
} from '@simplywallst/ui-core';
import ChevronDownSmallSolid from '@simplywallst/ui-core/icons/ChevronDownSmallSolid';
import PortfolioOutline from '@simplywallst/ui-core/icons/PortfolioOutline';

import CheckSolid from '@simplywallst/ui-core/icons/CheckSolid';
import LockSolid from '@simplywallst/ui-core/icons/LockSolid';

import { pluralise } from '@/utilities/pluralise';
import { usePortfolios } from '@/hooks/portfolio/usePortfolio';
import {
  ROUTE_MY_PORTFOLIOS,
  ROUTE_PORTFOLIO,
  ROUTE_PORTFOLIO_NEW,
} from '@/constants/routes';

import { PortfolioDropdownItem } from './PortfolioDropdownItem';

interface Props {
  portfolioId: string | null;
  children: ReactNode;
  onClick?: () => void;
}

export const PortfolioDropdown = ({
  portfolioId,
  children,
  onClick,
}: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { data: portfolios = [] } = usePortfolios();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'dropDown',
  });

  // RouterLink doesn't work in ButtonWithDropdown on mobile (native or screensize)
  const handleMobileSelect = useCallback(
    (value: string) => {
      if (value === 'all') return dispatch({ type: ROUTE_MY_PORTFOLIOS });
      if (value === 'new') return dispatch({ type: ROUTE_PORTFOLIO_NEW });

      dispatch({
        type: ROUTE_PORTFOLIO,
        payload: { portfolioId: value },
      });
    },
    [dispatch]
  );

  const options = useMemo(() => {
    const portfolioOptions = portfolios.map((portfolio) => {
      const excludedHoldings = portfolio.holdings.filter(
        (holding) => holding.isAnalysisRestricted === true
      );
      const holdings = portfolio.holdings.filter(
        (holding) => holding.isAnalysisRestricted === false
      );
      const numberOfHoldings = portfolio.holdings.length;
      const isPortfolioLimited = portfolio.isAnalysisRestricted;
      return (
        <PortfolioDropdownItem
          value={portfolio.id}
          key={`dropdown-item-${portfolio.id}`}
          isSelected={portfolio.id === portfolioId}
          data-cy-id={`dropdown-item-${portfolio.id}`}
          to={{
            type: ROUTE_PORTFOLIO,
            payload: { portfolioId: portfolio.id },
          }}
          onClick={() => {
            trackEvent({
              action: 'click',
              modifier: 'changePortfolio',
              portfolioId: portfolio.id,
            });
          }}
        >
          <Grid
            templateColumns="auto 40px"
            gap="8px"
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid templateColumns="36px auto" gap="8px" alignItems="start">
              <Grid
                minHeight="36px"
                minWidth="36px"
                alignItems="center"
                justifyContent="end"
              >
                {portfolio.id === portfolioId ? (
                  <CheckSolid
                    height="28"
                    width="28"
                    fill={theme.color.chart01}
                  />
                ) : null}
              </Grid>
              <Grid
                alignItems="start"
                paddingTop="x1"
                paddingBottom="x1"
                paddingRight="x1"
              >
                <Text
                  typography="xSmall"
                  color="phantom"
                  textAlign="left"
                  isTruncated
                >
                  {portfolio.name}
                </Text>
                <Text
                  typography="tiny"
                  color="darkGrayRGBA"
                  opacity={isPortfolioLimited ? 0.6 : 1}
                  textAlign="left"
                >
                  {!isPortfolioLimited && excludedHoldings.length > 0
                    ? `${holdings.length} ${pluralise(
                        holdings.length,
                        'holding',
                        'holdings'
                      )}, ${excludedHoldings.length} excluded`
                    : `${numberOfHoldings} ${pluralise(
                        numberOfHoldings,
                        'holding',
                        'holdings'
                      )}`}
                </Text>
              </Grid>
            </Grid>
            {isPortfolioLimited ? (
              <LockSolid
                aria-label="Blocked Portfolio"
                fill={theme.color.chart05}
              />
            ) : null}
          </Grid>
        </PortfolioDropdownItem>
      );
    });

    const numberOfPortfolios = portfolios.length;
    const allPortfoliosOption = (
      <PortfolioDropdownItem
        value="all"
        key="dropdown-item-all-portfolios"
        data-cy-id="dropdown-item-all-portfolios"
        to={{
          type: ROUTE_MY_PORTFOLIOS,
        }}
        onClick={() => {
          trackEvent({
            action: 'click',
            modifier: 'allPortfolios',
          });
        }}
      >
        <Grid templateColumns="36px max-content" gap="8px" alignItems="start">
          <Grid
            minHeight="36px"
            minWidth="36px"
            alignItems="center"
            justifyContent="end"
          >
            {portfolioId === null ? (
              <CheckSolid height="28" width="28" fill={theme.color.chart01} />
            ) : null}
          </Grid>
          <Grid
            alignItems="start"
            paddingTop="x1"
            paddingBottom="x1"
            paddingRight="x1"
          >
            <Text
              typography="xSmall"
              color="phantom"
              textAlign="left"
              isTruncated
            >
              All Portfolios
            </Text>
            <Text typography="tiny" color="darkGray" textAlign="left">
              {numberOfPortfolios}{' '}
              {pluralise(numberOfPortfolios, 'portfolio', 'portfolios')}
            </Text>
          </Grid>
        </Grid>
      </PortfolioDropdownItem>
    );

    return [allPortfoliosOption, ...portfolioOptions];
  }, [portfolios, portfolioId, trackEvent]);

  const secondaryOptions = useMemo(
    () => [
      <PortfolioDropdownItem
        key="dropdown-item-create"
        value="new"
        data-cy-id="dropdown-item-create"
        to={{ type: ROUTE_PORTFOLIO_NEW }}
        onClick={() => {
          trackEvent({
            action: 'click',
            modifier: 'createNewPortfolio',
          });
        }}
      >
        <Grid templateColumns="36px max-content" gap="8px" alignItems="start">
          <Grid
            minHeight="36px"
            minWidth="36px"
            alignItems="center"
            justifyContent="end"
          >
            <PortfolioOutline
              height="28"
              width="28"
              fill={theme.color.chart01}
            />
          </Grid>
          <Grid
            alignItems="start"
            paddingTop="x1"
            paddingBottom="x1"
            paddingRight="x1"
          >
            <Text typography="xSmall" color="spaceGray" isTruncated>
              Create new portfolio
            </Text>
          </Grid>
        </Grid>
      </PortfolioDropdownItem>,
    ],
    [trackEvent]
  );

  return (
    <Track>
      <Box position="relative" transform="translateX(-12px)" zIndex={2}>
        <ButtonWithDropdown
          data-cy-id="portfolio-dropdown"
          styleType="passiveLarge"
          floatLeft={true}
          onClick={onClick}
          onSelect={handleMobileSelect}
          options={options}
          secondaryOptions={secondaryOptions}
        >
          <Grid templateColumns="auto 24px" gap="8px" placeItems="center">
            {children}
            <ChevronDownSmallSolid fill={theme.color.whiteRGBA(0.5)} />
          </Grid>
        </ButtonWithDropdown>
      </Box>
    </Track>
  );
};

export default PortfolioDropdown;
